/**
 * Self-service password reset page — consumes the token emailed by
 * /api/auth/admin/forgot-password and applies the new password.
 *
 * UX rules:
 *  - Token is read from ?token=... in the URL (set by the email link)
 *  - Two password fields with show/hide toggle + visual strength meter
 *  - On submit, POST /api/auth/admin/reset-password
 *  - On success: toast + redirect to /login
 *  - On error: show inline error (backend returns 400 with explicit
 *    detail because the user is already inside the reset flow — no
 *    enumeration risk here)
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Lock, RefreshCw, AlertCircle, CheckCircle2 } from "lucide-react";
import { PasswordInput } from "../components/shared/PasswordInput";
import { API, LOJACK_LOGO } from "../config";
import { TelcaVoIPBadge } from "../components/shared/TelcaVoIPBadge";

function scorePassword(pw) {
  if (!pw) return { score: 0, label: "—" };
  let s = 0;
  if (pw.length >= 8) s++;
  if (pw.length >= 12) s++;
  if (/[A-Z]/.test(pw)) s++;
  if (/[a-z]/.test(pw)) s++;
  if (/[0-9]/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  const labels = ["Molto debole", "Debole", "Media", "Buona", "Forte", "Eccellente"];
  return { score: Math.min(s, labels.length - 1), label: labels[Math.min(s, labels.length - 1)] };
}

const AdminResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  // Guard: if the URL is missing ?token=... entirely, bounce the user
  // back to /login — they probably opened a malformed link.
  useEffect(() => {
    if (!token) {
      toast.error("Link di reset mancante o non valido.");
      navigate("/login", { replace: true });
    }
  }, [token, navigate]);

  const strength = useMemo(() => scorePassword(pw1), [pw1]);
  const mismatch = pw1.length > 0 && pw2.length > 0 && pw1 !== pw2;
  const tooShort = pw1.length > 0 && pw1.length < 8;
  const canSubmit = token && pw1 && pw2 && !mismatch && !tooShort;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/auth/admin/reset-password`, {
        token,
        new_password: pw1,
      });
      setDone(true);
      toast.success("Password aggiornata. Reindirizzamento al login...");
      setTimeout(() => navigate("/login", { replace: true }), 2000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Errore durante il reset");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-lojack-black flex items-center justify-center p-4" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={LOJACK_LOGO} alt="LoJack" className="h-14 mx-auto mb-6" />
          <h1 className="text-2xl font-bold text-white">Imposta una nuova password</h1>
          <p className="text-gray-400 mt-2">Secure Wallet — Reset Admin/IT</p>
        </div>

        <div className="wallet-card">
          {done ? (
            <div className="text-center py-4 space-y-3" data-testid="reset-success">
              <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto" />
              <p className="text-white font-semibold">Password aggiornata</p>
              <p className="text-gray-400 text-sm">Ti stiamo reindirizzando al login...</p>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-5">
              <div>
                <label className="block text-gray-400 text-sm mb-2">Nuova password</label>
                <PasswordInput
                  value={pw1}
                  onChange={e => setPw1(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 pr-12 text-white focus:border-lojack-red focus:outline-none"
                  placeholder="Almeno 8 caratteri"
                  required
                  minLength={8}
                  data-testid="reset-password-new"
                />
                {pw1 && (
                  <div className="mt-2">
                    <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all duration-300 ${
                          strength.score <= 1 ? "bg-red-500" :
                          strength.score <= 2 ? "bg-amber-500" :
                          strength.score <= 4 ? "bg-yellow-500" :
                          "bg-emerald-500"
                        }`}
                        style={{ width: `${((strength.score + 1) / 6) * 100}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Robustezza: <span className="text-gray-300">{strength.label}</span></p>
                  </div>
                )}
                {tooShort && (
                  <p className="text-red-400 text-xs mt-1">La password deve avere almeno 8 caratteri.</p>
                )}
              </div>

              <div>
                <label className="block text-gray-400 text-sm mb-2">Conferma password</label>
                <PasswordInput
                  value={pw2}
                  onChange={e => setPw2(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 pr-12 text-white focus:border-lojack-red focus:outline-none"
                  placeholder="Ripeti la password"
                  required
                  data-testid="reset-password-confirm"
                />
                {mismatch && (
                  <p className="text-red-400 text-xs mt-1">Le password non coincidono.</p>
                )}
              </div>

              {error && (
                <div className="flex items-start gap-2 bg-red-900/20 border border-red-700/40 rounded-lg p-3" data-testid="reset-error">
                  <AlertCircle className="h-4 w-4 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-red-300 text-sm">{error}</p>
                </div>
              )}

              <button
                type="submit"
                disabled={!canSubmit || submitting}
                className="w-full btn-lojack flex items-center justify-center gap-2 py-4 disabled:opacity-50"
                data-testid="reset-submit-btn"
              >
                {submitting ? <RefreshCw className="w-5 h-5 animate-spin" /> : <><Lock size={18} />Aggiorna password</>}
              </button>

              <p className="text-gray-500 text-xs text-center">
                Il link è valido 60 minuti ed è usabile una sola volta.
              </p>
            </form>
          )}
        </div>

        <div className="text-center mt-8">
          <TelcaVoIPBadge />
        </div>
      </div>
    </div>
  );
};

export default AdminResetPasswordPage;
